import React, { useEffect, useMemo, useState } from 'react';
import './AdminWallet.css';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../api';
import backIcon from '../../assets/backspace-icon.png';

function AdminWallet() {
  const navigate = useNavigate();
  const { search } = useLocation();

  // Route network (authoritative if present)
  const routeNetwork = React.useMemo(() => {
    const p = new URLSearchParams(search);
    const n = (p.get('network') || '').toLowerCase();
    return n === 'testnet' || n === 'mainnet' ? n : '';
  }, [search]);

  // -------- State --------
  const [balance, setBalance] = useState(0);
  const [holdBalance, setHoldBalance] = useState(0);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [walletAddress, setWalletAddress] = useState('—');
  const [copied, setCopied] = useState(false);


const [breakdown, setBreakdown] = useState({
  tips: 0,
  escrow: 0,
  sponsor: 0,
  subs: 0,
  stake: 0,
  backgroundStyles: 0,
  profileBanners: 0,
  total: 0,
});

  const [backendNetwork, setBackendNetwork] = useState(''); // 'testnet' | 'mainnet' | ''
  const [networkLabel, setNetworkLabel] = useState('');     // 'Testnet' | 'Mainnet'

  // ✅ period filter (today | 7d | 30d | all)
  const [period, setPeriod] = useState('all');

  // -------- Environment / Constants --------
  const TESTNET_WALLET = process.env.REACT_APP_PI_TESTNET_WALLET;
  const MAINNET_WALLET = process.env.REACT_APP_PI_MAINNET_WALLET;
  const LEGACY_WALLET  = process.env.REACT_APP_PI_APP_WALLET_ADDRESS; // optional fallback

  // Prefer explicit env flag like MyWallet
  const ENV_NETWORK =
    (process.env.REACT_APP_PI_NETWORK ||
      process.env.REACT_APP_ENV ||
      '').toLowerCase(); // 'testnet' | 'mainnet' | ''

  // Pi Browser sandbox (extra safety)
  const envSandbox =
    String(process.env.REACT_APP_PI_SANDBOX || '').toLowerCase() === 'true';

  const autoSandbox =
    (typeof window !== 'undefined' &&
      (window.location.hostname === 'sandbox.minepi.com' ||
       (window.location.origin || '').includes('sandbox.minepi.com'))) ||
    (typeof window !== 'undefined' && window.Pi?.environment === 'sandbox') ||
    (typeof document !== 'undefined' &&
      document.referrer?.includes('sandbox.minepi.com'));

  // Effective network decision (priority: route -> backend -> env -> sandbox)
  const effectiveNetwork = useMemo(() => {
    if (routeNetwork) return routeNetwork; // URL wins
    if (backendNetwork === 'testnet' || backendNetwork === 'mainnet') return backendNetwork;
    if (ENV_NETWORK === 'testnet') return 'testnet';
    if (ENV_NETWORK === 'mainnet') return 'mainnet';
    if (envSandbox || autoSandbox) return 'testnet';
    return 'mainnet';
  }, [routeNetwork, backendNetwork, ENV_NETWORK, envSandbox, autoSandbox]);

  // Pick wallet from effective network
  const sharedWallet = useMemo(() => {
    if (effectiveNetwork === 'testnet') {
      return TESTNET_WALLET || LEGACY_WALLET || '—';
    }
    return MAINNET_WALLET || LEGACY_WALLET || '—';
  }, [effectiveNetwork, TESTNET_WALLET, MAINNET_WALLET, LEGACY_WALLET]);

  useEffect(() => {
    setWalletAddress(sharedWallet);
    setNetworkLabel(effectiveNetwork === 'testnet' ? 'Testnet' : 'Mainnet');
  }, [sharedWallet, effectiveNetwork]);

  // Seed backendNetwork from route once (route is authoritative)
  useEffect(() => {
    if (routeNetwork) setBackendNetwork(routeNetwork);
  }, [routeNetwork]);

  useEffect(() => {
    const fetchAppWallet = async () => {
      try {
        const res = await api.get(
          `/admin/wallet?network=${effectiveNetwork}&period=${period}`
        );

        // totalBalance stays ALL-TIME from backend (even when period changes)
        setBalance(Number(res.data.totalBalance || 0));

        // these will change with period
        setHoldBalance(Number(res.data.holdBalance || 0));
        setAvailableBalance(Number(res.data.availableBalance || 0));
        setHistory(res.data.history || []);

// ---- revenue breakdown ----
const bk = res.data.breakdown || {};
const tips = Number(bk.tips || 0);
const escrow = Number(bk.escrow || 0);
const sponsor = Number(bk.sponsor || 0);
const subs = Number(bk.subs || 0);
const stake = Number(bk.stake || bk.stakeFees || bk.post_stake_fee || 0);



const backgroundStyles = Number(
  bk.backgroundStyles ||
  bk.background_styles ||
  bk.post_background_purchase ||
  0
);

const profileBanners = Number(
  bk.profileBanners ||
  bk.profile_banners ||
  bk.profile_banner_purchase ||
  0
);

const total = Number(
  bk.total ??
    (
      tips +
      escrow +
      sponsor +
      subs +
      stake +
      backgroundStyles +
      profileBanners
    )
);

setBreakdown({
  tips,
  escrow,
  sponsor,
  subs,
  stake,
  backgroundStyles,
  profileBanners,
  total,
});

   

     const net = (res.data.network || '').toLowerCase();
        // Only let backend “correct” us if route didn't force a network
        if (!routeNetwork && (net === 'testnet' || net === 'mainnet')) {
          setBackendNetwork(net);
        }
      } catch (err) {
        console.error('❌ Failed to load app wallet:', err);
      }
    };

    fetchAppWallet();
  }, [effectiveNetwork, routeNetwork, period]);

  const handleCopy = () => {
    if (walletAddress && walletAddress !== '—') {
      navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="adminwallet-container">
      {/* Topbar */}
      <div className="adminwallet-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="adminwallet-back-icon"
          onClick={() => navigate('/admin')}
        />
        <h2 onClick={() => navigate('/admin')} style={{ cursor: 'pointer' }}>
          BarterFi Wallet {networkLabel ? `(${networkLabel})` : ''}
        </h2>
      </div>

      {/* Scrollable Body */}
      <div className="adminwallet-body">

        {/* Period Filter (above Wallet Address) */}
        <div className="adminwallet-section">
          <p className="adminwallet-label">Period</p>
          <div className="adminwallet-period">
            <button
              className={`adminwallet-period-btn ${period === 'today' ? 'active' : ''}`}
              onClick={() => setPeriod('today')}
              type="button"
            >
              Today
            </button>
            <button
              className={`adminwallet-period-btn ${period === '7d' ? 'active' : ''}`}
              onClick={() => setPeriod('7d')}
              type="button"
            >
              7d
            </button>
            <button
              className={`adminwallet-period-btn ${period === '30d' ? 'active' : ''}`}
              onClick={() => setPeriod('30d')}
              type="button"
            >
              30d
            </button>
            <button
              className={`adminwallet-period-btn ${period === 'all' ? 'active' : ''}`}
              onClick={() => setPeriod('all')}
              type="button"
            >
              All
            </button>
          </div>
        </div>

        {/* Wallet Address */}
        <div className="adminwallet-section">
          <p className="adminwallet-label">Wallet Address</p>
          <div className="adminwallet-address-bar">
            <span className="wallet-address-text" title={walletAddress}>
              {walletAddress !== '—'
                ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
                : '—'}
            </span>
            <button onClick={handleCopy} type="button">
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Balances */}
        <div className="adminwallet-section">
          <p className="adminwallet-label">Total Balance</p>
          <h1 className="adminwallet-balance">{Number(balance).toFixed(2)} Pi</h1>
          <p className="adminwallet-subtext">Only Deposits and Withdrawals (all-time)</p>
        </div>

        <div className="adminwallet-section">
          <p className="adminwallet-label">Hold Balance</p>
          <h2 className="adminwallet-hold">{Number(holdBalance).toFixed(2)} Pi</h2>
          <p className="adminwallet-subtext">Temporary funds held before payout</p>
        </div>

        <div className="adminwallet-section">
          <p className="adminwallet-label">Available Balance</p>
          <h2 className="adminwallet-available">{Number(availableBalance).toFixed(2)} Pi</h2>
          <p className="adminwallet-subtext">Usable funds (profit / revenue)</p>
        </div>

        {/* Revenue Breakdown */}
        <div className="adminwallet-history-label">
          <p>Revenue Breakdown</p>
          <ul className="adminwallet-breakdown">
            <li>Tips Commission : <strong>{Number(breakdown.tips || 0).toFixed(2)} Pi</strong></li>
            <li>Escrow Commission: <strong>{Number(breakdown.escrow || 0).toFixed(2)} Pi</strong></li>
 

<li>
  Stake Commission:
  <strong>{Number(breakdown.stake || 0).toFixed(2)} Pi</strong>
</li>

<li>
  Background Styles:
  <strong>
    {Number(breakdown.backgroundStyles || 0).toFixed(2)} Pi
  </strong>
</li>

<li>
  Profile Banners:
  <strong>
    {Number(breakdown.profileBanners || 0).toFixed(2)} Pi
  </strong>
</li>

<li>
  Sponsorships:
  <strong>{Number(breakdown.sponsor || 0).toFixed(2)} Pi</strong>
</li>

<li>Subscriptions: <strong>{Number(breakdown.subs || 0).toFixed(2)} Pi</strong></li>
<li>Total Revenue: <strong>{Number(breakdown.total || 0).toFixed(2)} Pi</strong></li>

          </ul>
        </div>

        {/* Transaction History */}
        {history.length === 0 ? (
          <div className="adminwallet-history-placeholder">
            <p style={{ color: '#888' }}>No transactions yet.</p>
          </div>
        ) : (
          <div className="adminwallet-history-list">
            {history.map((tx, i) => {
              const type = String(tx.type || '').toLowerCase();

              // Orange for holds: escrow + stake
              const isHoldOrange =
                type === 'escrow_hold' ||
                type === 'stake_hold' ||
                type === 'post_stake_hold';


const isNegative =
  type === 'escrow_refund' ||
  type === 'post_background_refund' ||
  type === 'post_stake_refund' ||
  type === 'profile_banner_refund' ||
  Number(tx.amount) < 0;

              const amountClass = isHoldOrange
                ? 'adminwallet-amount orange'
                : isNegative
                ? 'adminwallet-amount red'
                : 'adminwallet-amount green';

              const signedAmount = isNegative
                ? `-${Math.abs(Number(tx.amount || 0))}`
                : `+${Number(tx.amount || 0)}`;

              return (
                <div className="adminwallet-history-row" key={i}>
                  <div className="adminwallet-date-type">
                    <strong>{tx.type}</strong>
                    <small>
                      {tx.timestamp
                        ? new Intl.DateTimeFormat('en-ZA', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          }).format(new Date(tx.timestamp))
                        : ''}
                    </small>
                  </div>

                  <span className={amountClass}>
                    {signedAmount} Pi
                  </span>

                  <span className="adminwallet-desc">{tx.description}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminWallet;
